import { NOT_AVAILABLE_TEXT } from 'app/utils/text/helpers'

export const ACT = 'ACT'
export const QLD = 'QLD'
export const NSW = 'NSW'
export const NT = 'NT'
export const SA = 'SA'
export const TAS = 'TAS'
export const VIC = 'VIC'
export const WA = 'WA'

export const AUSTRALIAN_CAPITAL_TERRITORY = 'AUSTRALIAN CAPITAL TERRITORY'
export const NEW_SOUTH_WALES = 'NEW SOUTH WALES'
export const NORTHERN_TERRITORY = 'NORTHERN TERRITORY'
export const QUEENSLAND = 'QUEENSLAND'
export const SOUTH_AUSTRALIA = 'SOUTH AUSTRALIA'
export const TASMANIA = 'TASMANIA'
export const VICTORIA = 'VICTORIA'
export const WESTERN_AUSTRALIA = 'WESTERN AUSTRALIA'

const shortAustralianStates = [ACT, NSW, NT, QLD, SA, TAS, VIC, WA]
const longAustralianStates = [
  AUSTRALIAN_CAPITAL_TERRITORY,
  NEW_SOUTH_WALES,
  NORTHERN_TERRITORY,
  QUEENSLAND,
  SOUTH_AUSTRALIA,
  TASMANIA,
  VICTORIA,
  WESTERN_AUSTRALIA,
]

export { shortAustralianStates }

// Mapping from long state names to short codes
const stateNameToCodeMap: Record<string, string> = {
  [AUSTRALIAN_CAPITAL_TERRITORY]: ACT,
  [NEW_SOUTH_WALES]: NSW,
  [NORTHERN_TERRITORY]: NT,
  [QUEENSLAND]: QLD,
  [SOUTH_AUSTRALIA]: SA,
  [TASMANIA]: TAS,
  [VICTORIA]: VIC,
  [WESTERN_AUSTRALIA]: WA,
}

// Normalize state to short form (e.g., "New South Wales" -> "NSW", "NSW" -> "NSW")
export const normalizeStateToShortForm = (state: string): string => {
  if (!state) return ''

  const upperState = state.toUpperCase().trim()

  // If it's already a short form, return it
  if (shortAustralianStates.includes(upperState)) {
    return upperState
  }

  // Try to find the short form from the long name mapping
  const shortForm = stateNameToCodeMap[upperState]
  if (shortForm) {
    return shortForm
  }

  // If not found, return the original value
  return state
}

const CountryAustralia = 'Australia'
const CountryAustraliaAU = 'AU'
const CountryAustraliaAUS = 'AUS'

export const australianStates =
  shortAustralianStates.concat(longAustralianStates)

const statesWhereBGCIsNotSupported = [
  SA,
  SOUTH_AUSTRALIA,
  NSW,
  NEW_SOUTH_WALES,
  VIC,
  VICTORIA,
]

export const checkIfBGCheckNotSupportedInState = (state: string) => {
  if (!state) {
    return false
  }

  return statesWhereBGCIsNotSupported.includes(state.toUpperCase())
}

const southAustraliaStateVariants = [SA, SOUTH_AUSTRALIA]

export const isSouthAustralia = (state: string) => {
  if (!state) {
    return false
  }

  return southAustraliaStateVariants.includes(state.toUpperCase())
}

interface Address {
  friendlyName?: string
  unit?: string
  unitNumber?: string
  number?: string
  streetNumber?: string
  streetName?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
}

type FormatAddressOptions = {
  fallbackToComponents?: boolean
  includeState?: boolean
  includePostcode?: boolean
  includeCountry?: boolean
}

function isAustralia(country: string) {
  return (
    country === CountryAustralia ||
    country === CountryAustraliaAU ||
    country === CountryAustraliaAUS
  )
}

const compactAddressParts = (parts: Array<string | undefined>): string[] =>
  parts.map((part) => (part || '').trim()).filter(Boolean)

const getComponentAddressLine = (address: Address): string => {
  const streetLabel = compactAddressParts([
    address.streetNumber || address.number,
    address.streetName,
  ]).join(' ')

  if (!streetLabel) {
    return ''
  }

  const unitNumber = address.unitNumber || address.unit
  return unitNumber ? `${unitNumber}/${streetLabel}` : streetLabel
}

export function formatAddress(
  address: Address | null | undefined,
  {
    fallbackToComponents = false,
    includeState = true,
    includePostcode = true,
    includeCountry = true,
  }: FormatAddressOptions = {},
) {
  if (!address) {
    return NOT_AVAILABLE_TEXT
  }

  const friendlyName = address.friendlyName?.trim()
  const primaryLine =
    friendlyName ||
    (fallbackToComponents ? getComponentAddressLine(address) : '')

  if (!primaryLine) {
    return NOT_AVAILABLE_TEXT
  }

  let formattedAddress = `${primaryLine}`
  if (address.suburb) {
    formattedAddress += `, ${address.suburb}`
  }
  if (includeState && address.state) {
    formattedAddress += ` ${address.state}`
  }
  if (includePostcode && address.postcode) {
    formattedAddress += ` ${address.postcode}`
  }
  if (includeCountry && address.country && !isAustralia(address.country)) {
    formattedAddress += `, ${address.country}`
  }

  return formattedAddress
}

export const isStateNSW = (state: string) => {
  if (!state) {
    return false
  }

  return state.toUpperCase() === NSW || state.toUpperCase() === NEW_SOUTH_WALES
}

const victoriaStateVariants = [VIC, VICTORIA]

export const isStateVIC = (state: string) => {
  if (!state) {
    return false
  }

  return victoriaStateVariants.includes(state.toUpperCase())
}

export const PROPERTY_ARRANGEMENT_TYPE = {
  I_RENTED: 1,
  I_BOARDED: 2,
  I_LIVED_WITH_FRIENDS: 3,
  I_LIVED_WITH_FAMILY: 4,
  I_OWNED_THE_PROPERTY: 5,
  I_SUBLETTED: 6,
  I_LIVED_WITH_FAMILY_OR_FRIENDS: 7,
  I_RENTED_WITH_AN_AGENT: 8,
  I_RENTED_WITH_PRIVATE_OWNER: 9,
  TEMPORARY_BNB: 10,
  NOT_DISCLOSED_RENTING: 11,
}

const QLD_INELIGIBLE_ARRANGEMENTS = [
  PROPERTY_ARRANGEMENT_TYPE.I_OWNED_THE_PROPERTY,
  PROPERTY_ARRANGEMENT_TYPE.TEMPORARY_BNB,
]

interface RentalHistory {
  propertyArrangement: number
}

export const isRentalHistoryEligibleForQLD = (
  rentalHistory: RentalHistory,
): boolean => {
  if (!rentalHistory || !rentalHistory.propertyArrangement) {
    return false
  }

  const { propertyArrangement } = rentalHistory

  if (
    propertyArrangement < PROPERTY_ARRANGEMENT_TYPE.I_RENTED ||
    propertyArrangement > PROPERTY_ARRANGEMENT_TYPE.NOT_DISCLOSED_RENTING
  ) {
    return false
  }

  return !QLD_INELIGIBLE_ARRANGEMENTS.includes(
    propertyArrangement as typeof QLD_INELIGIBLE_ARRANGEMENTS[number],
  )
}

export const isQLDRentalHistoryRequirementsMet = (
  rentalHistory: RentalHistory[] = [],
  noFurtherAddressHistory = false,
): boolean => {
  const eligibleRentalHistories = rentalHistory.filter(
    isRentalHistoryEligibleForQLD,
  )

  if (rentalHistory.length === 0) {
    return false
  }

  return eligibleRentalHistories.length >= 2 || noFurtherAddressHistory
}

/**
 * Converts any Australian state input (abbreviation or long name, case-insensitive) to the demonym (e.g., "Western Australian", "New South Welsh", "Queenslander").
 * Only uses state constants from this file.
 * @param {string} input - State abbreviation or name (e.g., "WA", "Western Australia")
 * @returns {string} - Demonym (e.g., "Western Australian"), or empty string if not recognised
 */
export function getStateDemonym(input: string): string {
  if (!input || typeof input !== 'string') return ''
  const normalised = input.trim().toUpperCase()
  const statesMap: Record<string, string> = {
    [NSW]: 'New South Welsh',
    [NEW_SOUTH_WALES]: 'New South Welsh',
    [VIC]: 'Victorian',
    [VICTORIA]: 'Victorian',
    [QLD]: 'Queenslander',
    [QUEENSLAND]: 'Queenslander',
    [WA]: 'Western Australian',
    [WESTERN_AUSTRALIA]: 'Western Australian',
    [SA]: 'South Australian',
    [SOUTH_AUSTRALIA]: 'South Australian',
    [TAS]: 'Tasmanian',
    [TASMANIA]: 'Tasmanian',
    [ACT]: 'Australian Capital Territory',
    [AUSTRALIAN_CAPITAL_TERRITORY]: 'Australian Capital Territory',
    [NT]: 'Northern Territorian',
    [NORTHERN_TERRITORY]: 'Northern Territorian',
  }

  return statesMap[normalised] || ''
}

/**
 * Converts any Australian state input (abbreviation or long name, case-insensitive) to the long state name (e.g., "VIC" → "Victoria", "NSW" → "New South Wales").
 * Only uses state constants from this file.
 * @param {string} input - State abbreviation or name (e.g., "WA", "Western Australia")
 * @returns {string} - Long state name (e.g., "Western Australia"), or empty string if not recognised
 */
export function getLongStateName(input: string): string {
  if (!input || typeof input !== 'string') return ''
  const normalised = input.trim().toUpperCase()
  const longNameMap: Record<string, string> = {
    [NSW]: 'New South Wales',
    [NEW_SOUTH_WALES]: 'New South Wales',
    [VIC]: 'Victoria',
    [VICTORIA]: 'Victoria',
    [QLD]: 'Queensland',
    [QUEENSLAND]: 'Queensland',
    [WA]: 'Western Australia',
    [WESTERN_AUSTRALIA]: 'Western Australia',
    [SA]: 'South Australia',
    [SOUTH_AUSTRALIA]: 'South Australia',
    [TAS]: 'Tasmania',
    [TASMANIA]: 'Tasmania',
    [ACT]: 'Australian Capital Territory',
    [AUSTRALIAN_CAPITAL_TERRITORY]: 'Australian Capital Territory',
    [NT]: 'Northern Territory',
    [NORTHERN_TERRITORY]: 'Northern Territory',
  }

  return longNameMap[normalised] || ''
}
